import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Save, RefreshCw, Plus, Trash2, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

export const HomepageManager = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await apiService.getSetting(token, 'homepage');
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateSetting(token, 'homepage', settings);
      toast.success('Homepage settings saved!');
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const addHeroImage = () => {
    setSettings({ ...settings, hero_images: [...(settings.hero_images || []), ''] });
  };

  const updateHeroImage = (index, value) => {
    const images = [...settings.hero_images];
    images[index] = value;
    setSettings({ ...settings, hero_images: images });
  };

  const removeHeroImage = (index) => {
    const images = settings.hero_images.filter((_, i) => i !== index);
    setSettings({ ...settings, hero_images: images });
  };

  const updateTrustBadge = (index, field, value) => {
    const badges = [...(settings.trust_badges || [])];
    badges[index] = { ...badges[index], [field]: value };
    setSettings({ ...settings, trust_badges: badges });
  };

  const addTrustBadge = () => {
    setSettings({ ...settings, trust_badges: [...(settings.trust_badges || []), { icon: '', label: '', desc: '' }] });
  };

  const removeTrustBadge = (index) => {
    const badges = settings.trust_badges.filter((_, i) => i !== index);
    setSettings({ ...settings, trust_badges: badges });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="homepage-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Homepage Manager</h1>
          <p className="text-slate-500">Customize your homepage content</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Hero Section */}
      <Card>
        <CardHeader>
          <CardTitle>Hero Section</CardTitle>
          <CardDescription>Main banner that visitors see first</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Hero Heading</Label>
            <Input
              value={settings?.hero_heading || ''}
              onChange={(e) => setSettings({ ...settings, hero_heading: e.target.value })}
              placeholder="Premium Aluminium, Glass & Front Elevation Solutions"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Hero Subheading</Label>
            <Textarea
              value={settings?.hero_subheading || ''}
              onChange={(e) => setSettings({ ...settings, hero_subheading: e.target.value })}
              placeholder="Describe your services..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Hero Images (Slideshow)</Label>
            {settings?.hero_images?.map((img, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={img}
                  onChange={(e) => updateHeroImage(index, e.target.value)}
                  placeholder="Image URL"
                />
                <Button variant="ghost" size="icon" onClick={() => removeHeroImage(index)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addHeroImage}>
              <Plus className="w-4 h-4 mr-1" /> Add Image
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Primary CTA Text</Label>
              <Input
                value={settings?.cta_primary_text || ''}
                onChange={(e) => setSettings({ ...settings, cta_primary_text: e.target.value })}
                placeholder="Call Now"
              />
            </div>
            <div className="space-y-2">
              <Label>Primary CTA Link</Label>
              <Input
                value={settings?.cta_primary_link || ''}
                onChange={(e) => setSettings({ ...settings, cta_primary_link: e.target.value })}
                placeholder="tel:08512853955"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Secondary CTA Text</Label>
              <Input
                value={settings?.cta_secondary_text || ''}
                onChange={(e) => setSettings({ ...settings, cta_secondary_text: e.target.value })}
                placeholder="Get Free Quote"
              />
            </div>
            <div className="space-y-2">
              <Label>Secondary CTA Link</Label>
              <Input
                value={settings?.cta_secondary_link || ''}
                onChange={(e) => setSettings({ ...settings, cta_secondary_link: e.target.value })}
                placeholder="/contact"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trust Badges */}
      <Card>
        <CardHeader>
          <CardTitle>Trust Badges</CardTitle>
          <CardDescription>Highlight your key strengths</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings?.trust_badges?.map((badge, index) => (
            <div key={index} className="p-4 border rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Badge {index + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeTrustBadge(index)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  value={badge.icon || ''}
                  onChange={(e) => updateTrustBadge(index, 'icon', e.target.value)}
                  placeholder="Icon name"
                />
                <Input
                  value={badge.label || ''}
                  onChange={(e) => updateTrustBadge(index, 'label', e.target.value)}
                  placeholder="Label"
                />
                <Input
                  value={badge.desc || ''}
                  onChange={(e) => updateTrustBadge(index, 'desc', e.target.value)}
                  placeholder="Description"
                />
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addTrustBadge}>
            <Plus className="w-4 h-4 mr-1" /> Add Badge
          </Button>
        </CardContent>
      </Card>

      {/* Sections Visibility */}
      <Card>
        <CardHeader>
          <CardTitle>Section Visibility</CardTitle>
          <CardDescription>Choose which sections to show</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Show Reviews Section</Label>
            <Switch
              checked={settings?.show_reviews_section}
              onCheckedChange={(checked) => setSettings({ ...settings, show_reviews_section: checked })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Show Projects Section</Label>
            <Switch
              checked={settings?.show_projects_section}
              onCheckedChange={(checked) => setSettings({ ...settings, show_projects_section: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Strip */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Strip</CardTitle>
          <CardDescription>Call-to-action strip at the bottom</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Heading</Label>
            <Input
              value={settings?.contact_strip_heading || ''}
              onChange={(e) => setSettings({ ...settings, contact_strip_heading: e.target.value })}
              placeholder="Ready to Transform Your Space?"
            />
          </div>
          <div className="space-y-2">
            <Label>Text</Label>
            <Input
              value={settings?.contact_strip_text || ''}
              onChange={(e) => setSettings({ ...settings, contact_strip_text: e.target.value })}
              placeholder="Get a free consultation..."
            />
          </div>
        </CardContent>
      </Card>

      {/* SEO */}
      <Card>
        <CardHeader>
          <CardTitle>SEO Settings</CardTitle>
          <CardDescription>Search engine optimization</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>SEO Title</Label>
            <Input
              value={settings?.seo_title || ''}
              onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })}
              placeholder="Page title for search engines"
            />
          </div>
          <div className="space-y-2">
            <Label>SEO Description</Label>
            <Textarea
              value={settings?.seo_description || ''}
              onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })}
              placeholder="Meta description for search engines"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-sky-500 hover:bg-sky-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

export default HomepageManager;
